import { css, html, LitElement, nothing } from 'lit'
import { property, state } from 'lit/decorators.js'
import { createRef, type Ref, ref } from 'lit/directives/ref.js'
import { maxWidth } from '@/utilities/breakpoints'
import { safeCustomElement } from '@/utilities/custom-element'
import type { CarouselNavPosition, CarouselNavType } from './types'

@safeCustomElement('mdc-carousel')
export class MdcCarousel extends LitElement {
  @property({ type: Number, attribute: 'items-per-page' }) itemsPerPage = 6
  @property({ type: String }) gap = '1rem'
  @property({ type: Boolean }) loop = false
  @property({ type: Number, attribute: 'arrow-offset' }) arrowOffset = 50
  @property({ type: String, attribute: 'nav-position' }) navPosition: CarouselNavPosition = 'inline'
  @property({ type: String, attribute: 'nav-type' }) navType: CarouselNavType = 'arrows'

  @state() private _currentPage = 0
  @state() private _totalPages = 0
  @state() private _items: Element[] = []
  @state() private _isMobile = false

  private _slotRef: Ref<HTMLSlotElement> = createRef()
  private _mediaQuery: MediaQueryList | null = null
  private _handleMediaChange = (e: MediaQueryListEvent | MediaQueryList) => {
    this._isMobile = e.matches
    this._updateVisibility()
  }

  connectedCallback() {
    super.connectedCallback()
    this._mediaQuery = window.matchMedia(maxWidth('md'))
    this._isMobile = this._mediaQuery.matches
    this._mediaQuery.addEventListener('change', this._handleMediaChange)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this._mediaQuery?.removeEventListener('change', this._handleMediaChange)
    this._mediaQuery = null
  }

  private _handleSlotChange() {
    const slot = this._slotRef.value
    if (!slot) return

    this._items = slot.assignedElements()
    this._totalPages = Math.ceil(this._items.length / this.itemsPerPage)

    if (this._currentPage >= this._totalPages) {
      this._currentPage = Math.max(0, this._totalPages - 1)
    }

    this._updateVisibility()
  }

  private _updateVisibility() {
    const startIndex = this._currentPage * this.itemsPerPage
    const endIndex = startIndex + this.itemsPerPage

    for (let i = 0; i < this._items.length; i++) {
      const el = this._items[i] as HTMLElement
      if (!this._usePagedLayout) {
        el.style.opacity = '1'
        el.style.visibility = 'visible'
        el.style.position = 'relative'
        el.style.flex = '0 0 75vw'
        el.style.scrollSnapAlign = 'start'
        el.removeAttribute('aria-hidden')
      } else if (i >= startIndex && i < endIndex) {
        el.style.opacity = '1'
        el.style.visibility = 'visible'
        el.style.position = 'relative'
        el.style.flex = ''
        el.style.scrollSnapAlign = ''
        el.removeAttribute('aria-hidden')
      } else {
        el.style.opacity = '0'
        el.style.visibility = 'hidden'
        el.style.position = 'absolute'
        el.style.flex = ''
        el.style.scrollSnapAlign = ''
        el.setAttribute('aria-hidden', 'true')
      }
    }
  }

  updated(changedProperties: Map<PropertyKey, unknown>) {
    if (
      changedProperties.has('_currentPage') ||
      changedProperties.has('itemsPerPage') ||
      changedProperties.has('_isMobile') ||
      changedProperties.has('navPosition')
    ) {
      this._totalPages = Math.ceil(this._items.length / this.itemsPerPage)

      if (this._currentPage >= this._totalPages) {
        this._currentPage = Math.max(0, this._totalPages - 1)
      }

      this._updateVisibility()
    }
  }

  private _handlePrev() {
    if (this._totalPages === 0) return

    if (this._currentPage > 0) {
      this._currentPage--
    } else if (this.loop) {
      this._currentPage = this._totalPages - 1
    }
    this._dispatchPageChange()
  }

  private _handleNext() {
    if (this._totalPages === 0) return

    if (this._currentPage < this._totalPages - 1) {
      this._currentPage++
    } else if (this.loop) {
      this._currentPage = 0
    }
    this._dispatchPageChange()
  }

  private _goToPage(page: number) {
    if (page < 0 || page >= this._totalPages) return
    this._currentPage = page
    this._dispatchPageChange()
  }

  private _dispatchPageChange() {
    this.dispatchEvent(
      new CustomEvent('page-change', {
        detail: { page: this._currentPage, totalPages: this._totalPages },
        bubbles: true,
        composed: true,
      })
    )
  }

  private _handleKeydown(e: KeyboardEvent) {
    if (e.key === 'ArrowLeft') {
      e.preventDefault()
      this._handlePrev()
    } else if (e.key === 'ArrowRight') {
      e.preventDefault()
      this._handleNext()
    }
  }

  private get _usePagedLayout() {
    return !this._isMobile || this.navPosition === 'below'
  }

  private get _clampedArrowOffset(): number {
    return Math.min(100, Math.max(0, this.arrowOffset))
  }

  private get _prevDisabled() {
    return !this.loop && this._currentPage === 0
  }

  private get _nextDisabled() {
    return !this.loop && this._currentPage >= this._totalPages - 1
  }

  static styles = css`
    :host {
      display: block;
      position: relative;
      font-family: var(--typography-font-family-sans);
    }

    .mdc-carousel {
      position: relative;
    }

    .mdc-carousel__track {
      display: grid;
      grid-template-columns: repeat(var(--_carousel-items-per-page, 6), 1fr);
      gap: var(--_carousel-gap, 1rem);
    }

    .mdc-carousel__track--mobile {
      display: flex;
      overflow-x: auto;
      scroll-snap-type: x mandatory;
      -webkit-overflow-scrolling: touch;
      gap: var(--_carousel-gap, 1rem);
      scrollbar-width: none;
    }

    .mdc-carousel__track--mobile::-webkit-scrollbar {
      display: none;
    }

    ::slotted(*) {
      transition: opacity 0.4s ease-in-out;
      min-width: 0;
    }

    .mdc-carousel__arrow {
      position: absolute;
      top: var(--_arrow-top, 50%);
      transform: translateY(-50%);
      z-index: 1;
    }

    .mdc-carousel__arrow--prev {
      left: -1.25rem;
    }

    .mdc-carousel__arrow--next {
      right: -1.25rem;
    }

    .mdc-carousel__nav-below {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0;
      margin-top: 1.5rem;
    }

    ::slotted([slot='nav-title']) {
      font-size: var(--typography-font-size-sm, 0.875rem);
      font-weight: var(--typography-font-weight-medium, 500);
      white-space: nowrap;
      margin: 0 1rem;
    }

    .mdc-carousel__dots {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .mdc-carousel__dot {
      width: 0.5rem;
      height: 0.5rem;
      border-radius: 50%;
      border: none;
      padding: 0;
      cursor: pointer;
      background: var(--color-border-default, #d1d5db);
      transition: background 0.2s ease, transform 0.2s ease;
    }

    .mdc-carousel__dot:hover {
      background: var(--color-border-emphasis, #9ca3af);
    }

    .mdc-carousel__dot:focus-visible {
      outline: 2px solid var(--color-border-emphasis, #9ca3af);
      outline-offset: 2px;
    }

    .mdc-carousel__dot--active {
      background: var(--color-fg-default, #111827);
      transform: scale(1.25);
      cursor: default;
      pointer-events: none;
    }

    /* breakpoints.md - 1 */
    @media (max-width: 767px) {
      .mdc-carousel__arrow {
        display: none;
      }

    }
  `

  private _renderInlineArrow(direction: 'prev' | 'next') {
    if (this.navPosition !== 'inline' || this._isMobile) return nothing

    const isPrev = direction === 'prev'
    const disabled = isPrev ? this._prevDisabled : this._nextDisabled
    if (disabled) return nothing

    return html`
      <div class="mdc-carousel__arrow mdc-carousel__arrow--${direction}">
        <mdc-icon-button
          type="primary"
          iconSize="default"
          size="lg"
          icon=${isPrev ? 'arrow-left' : 'arrow-right'}
          label=${isPrev ? 'Previous page' : 'Next page'}
          @click=${isPrev ? this._handlePrev : this._handleNext}
        ></mdc-icon-button>
      </div>
    `
  }

  private _renderBelowArrows() {
    return html`
      <div class="mdc-carousel__nav-below">
        <mdc-icon-button
          type="tertiary"
          iconSize="default"
          size="lg"
          icon="chevron-left"
          label="Previous page"
          ?disabled=${this._prevDisabled}
          @click=${this._handlePrev}
        ></mdc-icon-button>
        <slot name="nav-title"></slot>
        <mdc-icon-button
          type="tertiary"
          iconSize="default"
          size="lg"
          icon="chevron-right"
          label="Next page"
          ?disabled=${this._nextDisabled}
          @click=${this._handleNext}
        ></mdc-icon-button>
      </div>
    `
  }

  private _renderBelowDots() {
    const dots = Array.from({ length: this._totalPages }, (_, i) => {
      const isActive = i === this._currentPage
      return html`
        <button
          class="mdc-carousel__dot ${isActive ? 'mdc-carousel__dot--active' : ''}"
          role="tab"
          aria-selected=${isActive}
          aria-label="Go to page ${i + 1}"
          @click=${() => this._goToPage(i)}
        ></button>
      `
    })

    return html`
      <div class="mdc-carousel__nav-below">
        <div class="mdc-carousel__dots" role="tablist">
          ${dots}
        </div>
      </div>
    `
  }

  private _renderBelowNav() {
    if (this.navPosition !== 'below' || this._totalPages <= 1) return nothing
    return this.navType === 'dots' ? this._renderBelowDots() : this._renderBelowArrows()
  }

  render() {
    const trackClass =
      this._isMobile && !this._usePagedLayout
        ? 'mdc-carousel__track mdc-carousel__track--mobile'
        : 'mdc-carousel__track'

    return html`
      <div
        class="mdc-carousel"
        role="region"
        aria-roledescription="carousel"
        tabindex="0"
        style="--_arrow-top: ${this._clampedArrowOffset}%"
        @keydown=${this._handleKeydown}
      >
        ${this._renderInlineArrow('prev')}

        <div
          class=${trackClass}
          style="--_carousel-items-per-page: ${this.itemsPerPage}; --_carousel-gap: ${this.gap}"
          aria-live="polite"
        >
          <slot ${ref(this._slotRef)} @slotchange=${this._handleSlotChange}></slot>
        </div>

        ${this._renderInlineArrow('next')}
        ${this._renderBelowNav()}
      </div>
    `
  }
}
